<template>
  <div>
    <b-row class="mb-4">
      <b-col cols="12" offset-sm="1" sm="10">
        <h3>
          {{ $t('settings.project.segments.name') }}
        </h3>
        <hr>
        <p>
          {{ $t('settings.project.segments.description') }}
        </p>
      </b-col>
    </b-row>

    <b-row v-if="segments.length === 0" class="mb-4">
      <b-col cols="12" offset-sm="1" sm="10">
        <b-form-text>
          {{ $t('settings.project.segments.emptyState') }}
        </b-form-text>
      </b-col>
    </b-row>

    <b-row
      v-for="segment in segments"
      :key="segment.id"
      class="mb-3 align-items-center"
    >
      <b-col cols="12" offset-sm="1" sm="6">
        <b-form-input
          :value="segment.name"
          @change="renameSegment(segment.id, $event)"
        />
        <b-form-text>
          {{ $t('settings.project.segments.idHint') }}
          <code>{{ segment.id }}</code>
        </b-form-text>
      </b-col>
      <b-col cols="6" sm="2">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="editCondition(segment.id)"
        >
          {{ $t('settings.project.segments.conditionButton') }}
        </b-button>
      </b-col>
      <b-col cols="6" sm="2">
        <b-button
          variant="outline-danger"
          size="sm"
          @click="confirmRemoveSegment(segment.id)"
        >
          {{ $t('settings.project.segments.deleteButton') }}
        </b-button>
      </b-col>
      <!-- What the segment stands for, in words. This replaces the
           "N Bedingungen" count rather than joining it: the count said the
           same thing in a form nobody could act on, and it is contained in
           the sentence anyway (an empty segment says it is empty). Running
           text on its own line, not a heading and not squeezed into a narrow
           column, so a long condition stays readable when it wraps. text-break
           because a value can be one long word with nothing to break at — a
           URL, a long id — which would otherwise run out of the column. -->
      <b-col cols="12" offset-sm="1" sm="10" class="mt-1">
        <p class="mb-0 text-break">
          {{ sentenceForSegment(segment) }}
        </p>
      </b-col>
    </b-row>

    <b-row class="mb-4">
      <b-col cols="12" offset-sm="1" sm="5">
        <b-form-input
          v-model="newSegmentName"
          :placeholder="$t('settings.project.segments.namePlaceholder')"
          @keyup.enter="submitNewSegment"
        />
        <b-form-text v-if="duplicateWarning" class="text-danger">
          {{ $t('settings.project.segments.duplicateNameWarning') }}
        </b-form-text>
      </b-col>
      <b-col cols="12" sm="3">
        <b-button
          variant="outline-primary"
          @click="submitNewSegment"
        >
          {{ $t('settings.project.segments.addButton') }}
        </b-button>
      </b-col>
    </b-row>

    <!-- The condition as one readable line. It replaces the chain of three
         stacked modals this panel used to open (ModalPersonalizationEditor ->
         PageSettingsContext -> ModalCreateContextItem), which showed a segment
         a technical path, a source picker it did not need, and an action radio
         it had to be told to hide. The chain itself stays where it is still
         used: the old editor and the page settings. -->
    <segment-condition-editor
      :show="editingSegmentId !== null"
      :segment-name="editingSegmentName"
      :cases="editingSegmentCases"
      :groups="conditionFields"
      @close="cancelCondition"
      @apply="onConditionApplied"
    />
  </div>
</template>

<script>
import { cloneDeep, isEqual } from 'lodash';
import slugify from '@sindresorhus/slugify';
import { rowsFromCases } from '@/components/modals/segmentConditionModel';
import { collectAnswerFields } from '@/components/modals/answerFields';
import { collectConditionFields } from '@/components/modals/conditionFields';
import { sentenceFor } from '@/components/modals/conditionSentence';
import SegmentConditionEditor from './SegmentConditionEditor.vue';

// Final review C2. A segment's `cases` are evaluated by the reader through
// wbmgConditions.evaluate (files/_app_dev/js/conditions.js, resolveSegment),
// against the shared condition data object — NOT through
// contextApply.evaluateRule, whose query-string branch only ever sees
// top-level rules with source 'urlParameter'. A condition built on the address
// bar therefore has to be stored under the query string's own namespace in
// that data object ("urlParameter.<name>", see buildData); the bare parameter
// name has never been in it, so such a segment could not match, whatever URL
// the visitor arrived with.
//
// Both halves of that — putting the namespace on when a condition is written
// and taking it off again when it is read — live in segmentConditionModel.js,
// together with the reading of a stored condition as rows. One place that
// knows how a segment's condition is stored, shared by the sentence this panel
// shows and the editor that changes it.
//
// Top-level element/page rules with source 'urlParameter' keep their bare
// names and keep going through evaluateRule — that is the stored form of
// every such rule customers already have, and it is deliberately untouched.

// A segment's `cases` must be the { or: [ { and: [...] } ] } object shape
// the reader (files/_app_dev/js/conditions.js) hands straight to
// jsonLogic.apply. Anything else — the old Task 7 array shape, a missing
// field — is normalized to an empty condition rather than trusted, so a
// segment can never accidentally evaluate as "always true" (which an array
// shape does, since the reader has no `or`/`and` key to recognize).
//
// A hand-crafted or imported rule that IS valid json-logic but isn't already
// wrapped in { or: [...] } — e.g. the { and: [...] } shape
// conditionsSegments.spec.js's fixtures use — is wrapped rather than
// discarded: { or: [rule] } evaluates identically to `rule` alone (a single
// OR-alternative), so nothing valid is lost.
function isJsonLogicRule(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value)
    && Object.keys(value).length === 1;
}

function normalizeCases(cases) {
  if (cases && typeof cases === 'object' && !Array.isArray(cases) && Array.isArray(cases.or)) {
    return cases;
  }
  if (isJsonLogicRule(cases)) {
    return { or: [cases] };
  }
  return { or: [] };
}

// A form's field labels are stored per language handle, and the handle is
// 'de-DE' — never 'de'. Reading a label with the short code returns undefined,
// and collectAnswerFields then falls back to the block id, which is exactly how
// the editor once ended up showing "volume" where the question belonged.
const DEFAULT_FORM_LANGUAGE = 'de-DE';

// The lead-in of a segment's sentence. Hardcoded German like every other word
// the sentence is built from (see conditionSentence.js): the shared builder has
// no translation layer, and half a sentence in German with a translated opening
// would be worse than the one language this product has.
const SEGMENT_SENTENCE_LEAD = 'Trifft zu, wenn ';

// The name shown for the "use the personalization set itself as the context"
// shape, whose stored path is the fixed literal 'personalized_variable_set_id'
// and whose value is a set id. Same reason for being hardcoded German as above.
const PERSONALIZATION_SET_LABEL = 'Personalisierungs-Set';

export default {
  name: 'ProjectSettingsSegments',
  components: {
    SegmentConditionEditor,
  },
  props: {
    themeSettings: {
      type: Object,
      default: () => ({}),
    },
    // Real personalization sets and forms for the project's own item_path
    // (see ProjectSettings.vue's segmentPersonalizedData/segmentFormsData) —
    // the field list a condition can be built on is made of these, so answers
    // and personalization are genuinely available for a segment's condition,
    // not just the address bar.
    personalization: {
      type: Array,
      default: () => [],
    },
    formsData: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    segments: [],
    newSegmentName: '',
    duplicateWarning: false,
    // Id of the segment whose condition is open in the sentence editor, or
    // null when it is closed. Drives both whether the editor is shown and
    // which segment's `cases` gets written when it reports back.
    editingSegmentId: null,
  }),
  created() {
    this.loadFromThemeSettings();
  },
  watch: {
    // Final review I3. This panel keeps its own working copy of the list, so
    // it has to follow the prop whenever the surrounding view replaces it —
    // which "Abbrechen" (ProjectSettings.vue's resetSettings) does, by
    // refetching the project and rebuilding themeSettings from the database.
    // Without this, a redakteur who deleted a segment and then pressed
    // Abbrechen kept the shortened list on screen; the very next change in
    // this panel wrote that shortened list back, and the deleted segment was
    // gone for good — together with every condition referencing it by id,
    // which the reader silently resolves to false.
    //
    // A watcher, not a `:key` on the parent: the parent would have to know
    // when to bump the key (resetSettings does not touch componentKey today),
    // and re-creating the component throws away an open condition modal and a
    // half-typed segment name. This is the same shape as the Task 7 fix in
    // ProjectSettings.updateThemeSettings — a local copy that has to follow
    // its source of truth instead of drifting from it.
    themeSettings() {
      // The parent rebuilds `themeSettings` on every emitChange too. That
      // echo carries the very list this panel just sent, so rebuilding on it
      // would only replace identical objects with fresh clones — and would do
      // it in the middle of an edit. Compare first, act only on a real change.
      const incoming = Array.isArray(this.themeSettings && this.themeSettings.segments)
        ? this.themeSettings.segments
        : [];
      if (isEqual(incoming, this.segments)) return;
      this.loadFromThemeSettings();
    },
  },
  computed: {
    // Every answer field of every form of this project, flat, for naming a
    // 'form.<formId>.<fieldId>' path and for its field TYPE.
    //
    // collectAnswerFields takes ONE language, so it is called once PER FORM:
    // each form carries its own defaultLanguage, and one shared language would
    // label the fields of every form that does not use it with their block ids.
    //
    // Fix round 1: an empty entry is filtered out rather than guarded around.
    // collectAnswerFields reads `form.form_data` straight away, so a null in
    // the list throws — during RENDER, which costs the whole panel and every
    // healthy segment on it, not just this one form's fields.
    answerFields() {
      return (this.formsData || [])
        .filter((form) => form && typeof form === 'object')
        // `true`: the segment editor offers variables and "Formular
        // abgeschickt" through collectConditionFields, so a stored segment
        // rule may point at one -- and this lookup must find its type too.
        .flatMap((form) => collectAnswerFields(
          [form],
          (form.form_data && form.form_data.defaultLanguage) || DEFAULT_FORM_LANGUAGE,
          true,
        ));
    },
    answerFieldsByPath() {
      const byPath = {};
      this.answerFields.forEach((field) => { byPath[field.path] = field; });
      return byPath;
    },
    // Everything a condition can be built on, in the ONE list the editor's
    // condition panel offers too (collectConditionFields) — so a redakteur
    // picks a question by its wording here exactly as they do there.
    //
    // WITHOUT segments, and that is the whole reason the list is built here
    // rather than inside the editor: a segment must not be able to contain
    // itself, and this codebase has no ring protection to catch it if it did.
    //
    // An empty entry is filtered out for the same reason as in answerFields:
    // collectConditionFields reads `form.form_data` straight away, and a null
    // in the list would throw during render, costing the whole panel.
    // The address bar is offered even when the project has neither a form nor
    // a personalization set: "Kampagne Sommer" — one query-string parameter
    // and nothing else — is a whole segment on its own, and the old
    // three-modal chain could build it in a project without a single form.
    // The editor deliberately does NOT set this; see collectConditionFields.
    conditionFields() {
      return collectConditionFields({
        forms: (this.formsData || []).filter((form) => form && typeof form === 'object'),
        personalizationSets: this.personalization,
        allowUrlParameterAlone: true,
      });
    },
    editingSegment() {
      return this.segments.find((s) => s.id === this.editingSegmentId) || null;
    },
    editingSegmentName() {
      return this.editingSegment ? this.editingSegment.name : '';
    },
    // normalizeCases first, exactly as the sentence does it: the editor reads
    // the { or: [...] } shape and nothing else, and healing the older shapes
    // is this component's job, not the model's — two places repairing stored
    // shapes is how a round trip stops closing.
    editingSegmentCases() {
      return normalizeCases(this.editingSegment && this.editingSegment.cases);
    },
  },
  methods: {
    // Builds this panel's working copy from the `themeSettings` prop. Called
    // on creation and again whenever the surrounding view replaces the prop
    // (see the watcher above).
    loadFromThemeSettings() {
      const loadedSegments = Array.isArray(this.themeSettings && this.themeSettings.segments)
        ? cloneDeep(this.themeSettings.segments)
        : [];
      // A segment's cases must be the { or: [ { and: [...] } ] } object shape
      // the reader (files/_app_dev/js/conditions.js) evaluates via json-logic
      // — never an array. Task 7 normalized missing/invalid `cases` to `[]`,
      // before any condition modal existed to write real content into it; the
      // reader treats an array as an unrecognized operator and jsonLogic.apply
      // falls through to matching everything, so a segment stuck on that old
      // shape would silently apply on every page. normalizeCases migrates it.
      let migrated = false;
      this.segments = loadedSegments.map((segment) => {
        const cases = normalizeCases(segment.cases);
        // normalizeCases returns the SAME reference when the shape was already
        // correct — a changed reference means this segment's stored shape got
        // rewritten.
        if (cases !== segment.cases) migrated = true;
        return { ...segment, cases };
      });
      // Fix round 1: the migration above only ever touched the in-memory
      // copy. Nothing marked the surrounding project settings form as changed,
      // so a redakteur who opens this tab (or never even opens it — this
      // component mounts eagerly, see ProjectSettings.vue's un-lazy b-tabs)
      // and saves ANY other tab kept writing the dangerous old array shape
      // back to the database forever, even though the in-memory list already
      // looked correct. Emitting once here reuses the exact same save path
      // every other change in this panel already goes through.
      if (migrated) {
        this.emitChange();
      }
    },
    submitNewSegment() {
      this.addSegment(this.newSegmentName);
    },
    // The id is derived from the name exactly once, when the segment is
    // created — see idFromName. renameSegment() below never touches it:
    // conditions elsewhere reference a segment by id, and changing the id
    // on rename would silently break every one of them.
    addSegment(name) {
      const trimmedName = (name || '').trim();
      if (!trimmedName) {
        return;
      }
      const id = this.idFromName(trimmedName);
      if (!id || this.segments.some((segment) => segment.id === id)) {
        this.duplicateWarning = true;
        return;
      }
      this.duplicateWarning = false;
      this.segments.push({ id, name: trimmedName, cases: { or: [] } });
      this.newSegmentName = '';
      this.emitChange();
    },
    idFromName(name) {
      return slugify(name, { separator: '_' });
    },
    // Renames a segment WITHOUT ever changing its id — see addSegment above.
    renameSegment(id, newName) {
      const trimmedName = (newName || '').trim();
      if (!trimmedName) {
        return;
      }
      const segment = this.segments.find((s) => s.id === id);
      if (!segment) {
        return;
      }
      segment.name = trimmedName;
      this.emitChange();
    },
    // Fundstellen-Zaehlung ist nicht Teil dieser Aufgabe (Roadmap) — der
    // Hinweis warnt generisch, ohne zu zaehlen, wie viele Bedingungen
    // betroffen sind.
    confirmRemoveSegment(id) {
      this.$bvModal.msgBoxConfirm(this.$t('settings.project.segments.deleteConfirmText'), {
        title: this.$t('settings.project.segments.deleteConfirmTitle'),
        okTitle: this.$t('buttons.ok'),
        cancelTitle: this.$t('buttons.cancel'),
        centered: true,
      }).then((confirmed) => {
        if (confirmed) {
          this.removeSegment(id);
        }
      }).catch((err) => {
        console.log(err);
      });
    },
    removeSegment(id) {
      this.segments = this.segments.filter((segment) => segment.id !== id);
      this.emitChange();
    },
    // Opens the sentence editor on this segment's condition.
    editCondition(id) {
      this.editingSegmentId = id;
    },
    cancelCondition() {
      this.editingSegmentId = null;
    },
    // The finished `cases`, exactly as the editor built them — one flat
    // { or: [...] }, with the address-bar namespace already on and every row
    // the editor did not touch written back in the shape it was found in
    // (segmentConditionModel.js). Nothing is merged, split or rewritten here:
    // the old chain needed all of that because it edited an ARRAY of
    // per-source entries, and every one of those steps was a place where a
    // condition could be lost. A segment has one condition; so has this.
    onConditionApplied(cases) {
      const segment = this.editingSegment;
      if (segment) {
        segment.cases = cases;
        this.emitChange();
      }
      this.editingSegmentId = null;
    },
    // -----------------------------------------------------------------------
    // Reading a segment back as a German sentence.
    //
    // Everything below only LOOKS at a segment. Nothing here rebuilds an
    // operation, writes a normalized shape back or emits `change` — a stored
    // '!!' from before 0 and "Nein" counted as answered must survive being
    // read, or a segment would judge its visitors differently for no reason
    // other than that somebody opened this tab. `rowsFromCases` keeps every
    // operation it read on the row (`original`) precisely so that reading can
    // stay reading; only casesFromRows ever writes, and this method never
    // calls it.
    // -----------------------------------------------------------------------

    // The finished sentence for one segment, e.g.
    // "Trifft zu, wenn Fallvolumen pro Jahr größer als 10.000 ist."
    //
    // normalizeCases first: it heals the shapes older storage can still hold
    // (the Task 7 array, a bare rule) into the { or: [...] } that
    // rowsFromCases reads. Deliberately NOT done inside the model — two places
    // repairing stored shapes is how a round trip stops closing.
    sentenceForSegment(segment) {
      const groups = rowsFromCases(
        normalizeCases(segment && segment.cases),
        // The field TYPE behind a stored path, so a number reads "10.000" and
        // a text answer that happens to look like one stays "10000".
        (path) => {
          const field = this.answerFieldsByPath[path];
          return field && field.type;
        },
      );
      return sentenceFor({
        lead: String(this.$i18n && this.$i18n.locale).startsWith('en') ? 'Applies if ' : SEGMENT_SENTENCE_LEAD,
        groups,
        resolve: (row) => this.fieldForRow(row),
        language: this.$i18n && this.$i18n.locale,
      });
    },

    // What the sentence builder asks for when it needs the NAME behind a row
    // (and the labels of its options): `{ label, options? }`, or null when
    // there is nothing to name any more.
    fieldForRow(row) {
      if (row.kind === 'segment') {
        const segment = this.segments.find((s) => s.id === row.segmentId);
        return segment ? { label: segment.name } : null;
      }
      if (row.kind === 'form') {
        return this.answerFieldsByPath[row.path] || null;
      }
      if (row.kind === 'personalization') {
        return this.personalizationFieldForPath(row.path);
      }
      // 'urlParameter': there is no field list to look a parameter up in, so
      // the stored name IS the name — the sentence builder handles that itself.
      return null;
    },

    // A personalization path in either of its two stored shapes — the normal
    // "<setId>.<fieldId>", and the fixed literal 'personalized_variable_set_id'
    // that "use the set as the context" writes, whose value is a set id (which
    // is why the sets are offered as options: the sentence should name the set,
    // not print its number).
    personalizationFieldForPath(path) {
      const sets = this.personalization || [];
      if (path === 'personalized_variable_set_id') {
        return {
          label: PERSONALIZATION_SET_LABEL,
          options: sets.map((set) => ({
            value: set.personalized_variable_set_id,
            label: set.name,
          })),
        };
      }
      const [setId, fieldId] = path.split('.');
      const set = sets.find((s) => String(s.personalized_variable_set_id) === setId);
      if (!set) return null;
      const field = (set.schema || []).find((item) => item.id === fieldId);
      if (!field) return null;
      // Same "<Set>:<Feld>" wording the condition list already uses for a
      // personalization path (ModalPersonalizationEditor's printName filter).
      return { label: `${set.name}:${field.name}` };
    },
    emitChange() {
      this.$emit('change', { segments: this.segments });
    },
  },
};
</script>
